import { Utils, Fn } from './utils';
import { Floorplan } from './floorplan';
import { Wall } from './wall';
import { CornerDTO } from 'src/app/models/product-models/DTOs/CornerDTO';

/**
 * Corners are used to define Walls.
 */
export class Corner {
  private _cornerTolerance = 20;

  private _height = 250;

  /** Array of start walls. */
  public wallStarts: Wall[] = [];

  /** Array of end walls. */
  public wallEnds: Wall[] = [];

  /** Callbacks to be fired on movement. */
  private _movedCallbacks: Fn[] = [];

  /** Callbacks to be fired on removal. */
  private _deletedCallbacks: Fn[] = [];

  /** Callbacks to be fired in case of action. */
  private _actionCallbacks: Fn[] = [];

  public uuid: string;
  /** Constructs a corner.
   * @param floorplan The associated floorplan.
   * @param x X coordinate.
   * @param y Y coordinate.
   * @param z Z coordinate.
   * @param id An optional unique id. If not set, created internally.
   */
  constructor(
    private floorplan: Floorplan,
    public x: number,
    public y: number,
    public z: number,
    uuid?: string,
  ) {
    this.uuid = uuid || Utils.guid();
  }

  public getCornerTolerance() {
    return this._cornerTolerance;
  }

  public setCornerTolerance(value: number) {
    this._cornerTolerance = value;
  }

  public toDTO() {
    return new CornerDTO(this.uuid, this.getX(), this.getY(), this.getZ(), this.getHeight());
  }

  /** Add function to moved callbacks.
   * @param func The function to be added.
   */
  public fireOnMove(func: Fn) {
    this._movedCallbacks.push(func);
  }

  /** Add function to deleted callbacks.
   * @param func The function to be added.
   */
  public fireOnDelete(func: Fn) {
    this._deletedCallbacks.push(func);
  }

  /** Add function to action callbacks.
   * @param func The function to be added.
   */
  public fireOnAction(func: Fn) {
    this._actionCallbacks.push(func);
  }

  public getX(): number {
    return this.x;
  }

  public getY(): number {
    return this.y;
  }

  public getZ(): number {
    return this.z;
  }

  public getHeight(): number {
    return this._height;
  }

  public setHeight(val: number) {
    this._height = val;
  }

  public snapToAxis(tolerance: number): { x: boolean; y: boolean } {
    // try to snap this corner to an axis
    const snapStatus = {
      x: false,
      y: false,
    };

    this.adjacentCorners().forEach((corner) => {
      if (Math.abs(corner.x - this.x) < tolerance) {
        this.x = corner.x;
        snapStatus.x = true;
      }
      if (Math.abs(corner.y - this.y) < tolerance) {
        this.y = corner.y;
        snapStatus.y = true;
      }
    });

    if (snapStatus.x || snapStatus.y) {
      this.wallStarts.forEach((wall) => {
        wall.fireMoved();
      });

      this.wallEnds.forEach((wall) => {
        wall.fireMoved();
      });
    }

    return snapStatus;
  }

  /** Moves corner relatively to new position.
   * @param dx The delta x.
   * @param dy The delta y.
   */
  public relativeMove(dx: number, dy: number) {
    this.move(this.x + dx, this.y + dy);
  }

  private fireAction(action: Fn) {
    //this.action_callbacks.fire(action)
    this._actionCallbacks.forEach((callback: Fn) => callback(action));
  }

  /** Remove callback. Fires the delete callbacks. */
  public remove() {
    //this.deleted_callbacks.fire(this);
    this._deletedCallbacks.forEach((callback: Fn) => callback(this));
  }

  /** Removes all walls. */
  public removeAll() {
    for (let i = 0; i < this.wallStarts.length; i++) this.wallStarts[i].remove();
    for (let i = 0; i < this.wallEnds.length; i++) this.wallEnds[i].remove();
    this.remove();
  }

  public resetBlockedWalls() {
    this._blockedWalls = [];
  }

  /** Moves corner to new position.
   * @param newX The new x position.
   * @param newY The new y position.
   */
  public move(newX: number, newY: number) {
    if (this.blockCornerMovement(newX, newY)) return;

    const oldX = this.x;
    const oldY = this.y;

    this.x = newX;
    this.y = newY;

    this.limitWallLength(oldX, oldY);

    this.mergeWithIntersected();

    //this.moved_callbacks.fire(this.x, this.y);
    this._movedCallbacks.forEach((callback: Fn) => callback(this.x, this.y));

    this.wallStarts.forEach((wall) => {
      wall.fireMoved();
    });

    this.wallEnds.forEach((wall) => {
      wall.fireMoved();
    });
  }

  /** Gets the adjacent corners.
   * @returns Array of corners.
   */
  public adjacentCorners(): Corner[] {
    const retArray = [];

    for (let i = 0; i < this.wallStarts.length; i++) retArray.push(this.wallStarts[i].getEnd());
    for (let i = 0; i < this.wallEnds.length; i++) retArray.push(this.wallEnds[i].getStart());

    return retArray;
  }

  private isWallConnected(wall: Wall): boolean {
    for (let i = 0; i < this.wallStarts.length; i++) {
      if (this.wallStarts[i] === wall) {
        return true;
      }
    }
    for (let i = 0; i < this.wallEnds.length; i++) {
      if (this.wallEnds[i] === wall) {
        return true;
      }
    }

    return false;
  }

  private _blockedWalls: { wall: Wall; maxLength: number }[] = [];

  private blockCornerMovement(x: number, y: number): boolean {
    const walls = this.floorplan.getWalls();

    for (const wall of walls) {
      for (const object of wall.getObjects()) {
        const distance = (object.width * object.pixelsPerCm) / 2;

        if (object.distanceFrom(x, y) <= distance) {
          this._blockedWalls.push({
            wall: wall,
            maxLength: wall.getLength(),
          });

          return true;
        }
      }
    }

    return false;
  }

  private limitWallLength(x: number, y: number) {
    const walls = this.floorplan.getWalls();

    for (const wall of walls) {
      const blockedWall = this._blockedWalls.find((w) => w.wall === wall);

      if (blockedWall && wall.getLength() < blockedWall.maxLength) {
        this.x = x;
        this.y = y;
      }
    }
  }

  /**
   *
   */
  public distanceFrom(x: number, y: number): number {
    return Utils.distance(x, y, this.x, this.y);
  }

  /** Gets the distance from a wall.
   * @param wall A wall.
   * @returns The distance.
   */
  public distanceFromWall(wall: Wall): number {
    return wall.distanceFrom(this.x, this.y);
  }

  /** Gets the distance from a corner.
   * @param corner A corner.
   * @returns The distance.
   */
  public distanceFromCorner(corner: Corner): number {
    return this.distanceFrom(corner.x, corner.y);
  }

  /** Detaches a wall.
   * @param wall A wall.
   */
  public detachWall(wall: Wall) {
    Utils.removeValue(this.wallStarts, wall);
    Utils.removeValue(this.wallEnds, wall);
    if (this.wallStarts.length === 0 && this.wallEnds.length === 0) {
      this.remove();
    }
  }

  /** Attaches a start wall.
   * @param wall A wall.
   */
  public attachStart(wall: Wall) {
    this.wallStarts.push(wall);
  }

  /** Attaches an end wall.
   * @param wall A wall.
   */
  public attachEnd(wall: Wall) {
    this.wallEnds.push(wall);
  }

  /** Get wall to corner.
   * @param corner A corner.
   * @return The associated wall or null.
   */
  public wallTo(corner: Corner): Wall | null {
    for (let i = 0; i < this.wallStarts.length; i++) {
      if (this.wallStarts[i].getEnd() === corner) {
        return this.wallStarts[i];
      }
    }
    return null;
  }

  /** Get wall from corner.
   * @param corner A corner.
   * @return The associated wall or null.
   */
  public wallFrom(corner: Corner): Wall | null {
    for (let i = 0; i < this.wallEnds.length; i++) {
      if (this.wallEnds[i].getStart() === corner) {
        return this.wallEnds[i];
      }
    }
    return null;
  }

  /** Get wall to or from corner.
   * @param corner A corner.
   * @return The associated wall or null.
   */
  public wallToOrFrom(corner: Corner): Wall | null {
    return this.wallTo(corner) || this.wallFrom(corner);
  }

  /**
   *
   */
  private combineWithCorner(corner: Corner) {
    // update position to other corner's
    this.x = corner.x;
    this.y = corner.y;
    // absorb the other corner's wallStarts and wallEnds
    for (let i = corner.wallStarts.length - 1; i >= 0; i--) {
      corner.wallStarts[i].setStart(this);
    }
    for (let i = corner.wallEnds.length - 1; i >= 0; i--) {
      corner.wallEnds[i].setEnd(this);
    }
    // delete the other corner
    corner.removeAll();
    this.removeDuplicateWalls();
    this.floorplan.update();
  }

  public mergeWithIntersected(): boolean {
    //console.log('mergeWithIntersected for object: ' + this.type);
    // check corners
    for (let i = 0; i < this.floorplan.getCorners().length; i++) {
      const corner = this.floorplan.getCorners()[i];
      if (this.distanceFromCorner(corner) < this._cornerTolerance && corner !== this) {
        this.combineWithCorner(corner);
        return true;
      }
    }
    // check walls
    for (let i = 0; i < this.floorplan.getWalls().length; i++) {
      const wall = this.floorplan.getWalls()[i];
      if (this.distanceFromWall(wall) < this._cornerTolerance && !this.isWallConnected(wall)) {
        // update position to be on wall
        const intersection = Utils.closestPointOnLine(
          this.x,
          this.y,
          wall.getStart().x,
          wall.getStart().y,
          wall.getEnd().x,
          wall.getEnd().y,
        );
        this.x = intersection.x;
        this.y = intersection.y;
        // merge this corner into wall by breaking wall into two parts
        this.floorplan.newWall(this, wall.getEnd(), wall.getObjects());
        wall.setEnd(this);
        this.removeDuplicateWalls();
        this.floorplan.update();
        return true;
      }
    }
    return false;
  }

  /** Ensure we do not have duplicate walls (i.e. same start and end points) */
  private removeDuplicateWalls() {
    // delete the wall between these corners, if it exists
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wallEndpoints: any = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wallStartpoints: any = {};

    for (let i = this.wallStarts.length - 1; i >= 0; i--) {
      if (this.wallStarts[i].getEnd() === this) {
        // remove zero length wall
        this.wallStarts[i].remove();
      } else if (this.wallStarts[i].getEnd().uuid in wallEndpoints) {
        // remove duplicated wall
        this.wallStarts[i].remove();
      } else {
        wallEndpoints[this.wallStarts[i].getEnd().uuid] = true;
      }
    }
    for (let i = this.wallEnds.length - 1; i >= 0; i--) {
      if (this.wallEnds[i].getStart() === this) {
        // removed zero length wall
        this.wallEnds[i].remove();
      } else if (this.wallEnds[i].getStart().uuid in wallStartpoints) {
        // removed duplicated wall
        this.wallEnds[i].remove();
      } else {
        wallStartpoints[this.wallEnds[i].getStart().uuid] = true;
      }
    }
  }

  public clone() {
    return new Corner(this.floorplan, this.x, this.y, this.z);
  }
}
